<template>
  <div class="bg-white rounded-md shadow-md">
    <div class="flex p-3 items-center justify-between border-b-2 border-dashed">
      <div class="flex items-center justify-start gap-2">
        <div
          class="min-w-7 w-7 h-7 rounded-md flex justify-center items-center"
          :class="bgColorIcon"
        >
          <img
            :src="require(`../../../assets/icon/${icon}`)"
            alt="icon-fuel"
            class="w-4 h-auto"
            loading="eager"
          />
        </div>
        <h5 class="text-xs font-semibold text-gray-800">
          {{ title }}
        </h5>
      </div>
      <div class="flex items-center justify-end gap-3">
        <div class="rounded-md bg-slate-50 p-1 flex gap-2">
          <div
            class="px-2 py-1 cursor-pointer"
            v-for="data in chartViews"
            :key="data.id"
            :class="{
              'bg-white shadow-md rounded': tabActive === data.id,
            }"
            @click="changeView(data.id)"
          >
            <h5 class="text-[10px] font-semibold text-slate-800">
              {{ data.title }}
            </h5>
          </div>
        </div>
        <div
          class="w-5 h-5 rounded-md bg-gray-500 flex justify-center items-center cursor-pointer"
          @click="$router.push(`/dashboard-esg/${type}`)"
          v-if="!isLarge"
        >
          <img
            src="../../../assets/icon/arrow.svg"
            alt="arrow"
            class="rotate-180 w-4 h-auto"
          />
        </div>
        <v-btn
          density="compact"
          variant="outlined"
          class="capitalize font-medium text-[10px] border-[2px] text-slate-600 border-slate-300"
          v-if="isLarge && type === 'FUEL' && isAdmin"
          @click="getCompare"
        >
          <img
            src="./../../../assets/icon/fatrows.png"
            alt="Compare Icon"
            class="mr-2 h-3 w-auto"
          />
          Lihat perbandingan
          <img
            src="./../../../assets/icon/close.png"
            alt="Close Icon"
            class="ml-2 h-3 w-auto"
            v-if="compare"
          />
        </v-btn>
      </div>
    </div>

    <v-card elevation="0" :loading="loading" class="px-3 pb-3">
      <v-checkbox
        v-model="legend.find((data) => data.id === idRatioLegend).show"
        hide-details
        density="compact"
        color="#dc2626"
        v-if="!compare"
      >
        <template v-slot:label>
          <h6 class="text-xs font-medium ml-1">Show Fuel Ratio</h6>
        </template>
      </v-checkbox>

      <div class="flex justify-between items-center pt-3 px-2" v-if="compare">
        <v-autocomplete
          variant="outlined"
          hide-details
          color="#475569"
          density="compact"
          placeholder="Semua Mitra"
          class="max-w-56"
          :items="initialData"
          item-title="title"
          item-value="value"
          v-model="mitra"
          multiple
          chips
          closable-chips
          @update:model-value="getCompareChart(mitra)"
        >
        </v-autocomplete>
        <div class="flex items-center gap-4">
          <v-checkbox
            v-model="displayCompare.fuel"
            hide-details
            density="compact"
            color="#dc2626"
          >
            <template v-slot:label>
              <h6 class="text-xs font-medium ml-1">Fuel</h6>
            </template>
          </v-checkbox>
          <v-checkbox
            v-model="displayCompare.distance"
            hide-details
            density="compact"
            color="#dc2626"
          >
            <template v-slot:label>
              <h6 class="text-xs font-medium ml-1">Distance</h6>
            </template>
          </v-checkbox>
          <v-checkbox
            v-model="displayCompare.ob_coal"
            hide-details
            density="compact"
            color="#dc2626"
          >
            <template v-slot:label>
              <h6 class="text-xs font-medium ml-1">OB/Coal</h6>
            </template>
          </v-checkbox>
        </div>
      </div>

      <template v-if="!compare">
        <div
          class="w-full my-4 flex items-end border-b relative"
          :style="{ height: height + 'px' }"
        >
          <svg
            class="absolute w-full h-full pointer-events-none"
            style="z-index: 13"
            v-if="legend.find((data) => data.id === idRatioLegend).show"
          >
            <polyline
              :points="polylinePoints"
              fill="none"
              stroke="#1e3a8a"
              stroke-width="3"
              stroke-linecap="round"
              stroke-linejoin="round"
              ref="polyline"
            />
          </svg>

          <div
            :class="{
              'px-4 w-1/5': tabActive === 1,
              'px-[2px] w-1/12': tabActive === 2,
            }"
            v-for="(item, index) in data"
            :key="index"
            ref="blocks"
          >
            <div
              class="w-full relative overflow-visible"
              :style="{ height: height + 'px' }"
            >
              <!-- block -->
              <div
                class="w-4/5 bg-slate-300 absolute left-1/2 -translate-x-1/2 bottom-0 transition-all duration-300 ease-in-out"
                :class="{
                  'max-w-20': tabActive === 1 && isLarge,
                  'max-w-14': tabActive === 1 && !isLarge,
                  'max-w-12': tabActive === 2,
                }"
                :style="{
                  height: calculateHeight(item.production) + 'px',
                  'z-index': calculateZIndex(item).production,
                }"
              ></div>
              <div
                class="w-4/5 bg-amber-500 absolute left-1/2 -translate-x-1/2 bottom-0 transition-all duration-300 ease-in-out"
                :class="{
                  'max-w-20': tabActive === 1 && isLarge,
                  'max-w-14': tabActive === 1 && !isLarge,
                  'max-w-12': tabActive === 2,
                }"
                :style="{
                  height: calculateHeight(item.val_amber) + 'px',
                  'z-index': calculateZIndex(item).val_amber,
                }"
              ></div>
              <div
                class="w-4/5 bg-red-600 absolute left-1/2 -translate-x-1/2 bottom-0 transition-all duration-300 ease-in-out"
                :class="{
                  'max-w-20': tabActive === 1 && isLarge,
                  'max-w-14': tabActive === 1 && !isLarge,
                  'max-w-12': tabActive === 2,
                }"
                :style="{
                  height: calculateHeight(item.val_red) + 'px',
                  'z-index': calculateZIndex(item).val_red,
                }"
              ></div>
              <div
                class="w-3/4 bg-orange-200 absolute left-1/2 -translate-x-1/2 bottom-0 transition-all duration-300 ease-in-out"
                :class="{
                  'max-w-[70px]': tabActive === 1 && isLarge,
                  'max-w-12': tabActive === 1 && !isLarge,
                  'max-w-10': tabActive === 2,
                }"
                :style="{
                  height: calculateHeight(item.val_orange) + 'px',
                  'z-index': calculateZIndex(item).val_orange,
                }"
              ></div>
              <div
                class="w-8/12 bg-emerald-600 absolute left-1/2 -translate-x-1/2 bottom-0 transition-all duration-300 ease-in-out"
                :class="{
                  'max-w-[70px]': tabActive === 1 && isLarge,
                  'max-w-12': tabActive === 1 && !isLarge,
                  'max-w-10': tabActive === 2,
                }"
                :style="{
                  height: calculateHeight(item.val_emerald) + 'px',
                  'z-index': calculateZIndex(item).val_emerald,
                }"
              ></div>
              <div
                class="w-8/12 bg-orange-300 absolute left-1/2 -translate-x-1/2 bottom-0 transition-all duration-300 ease-in-out"
                :class="{
                  'max-w-[70px]': tabActive === 1 && isLarge,
                  'max-w-12': tabActive === 1 && !isLarge,
                  'max-w-10': tabActive === 2,
                }"
                :style="{
                  height: calculateHeight(item.val_yellow) + 'px',
                  'z-index': calculateZIndex(item).val_yellow,
                }"
              ></div>
              <!-- label -->
              <div
                v-if="(item.production ?? 0) !== 0 && height"
                class="absolute rounded-lg bg-slate-300 z-10"
                :class="{
                  'px-1 py-[2px]': tabActive === 1,
                  'px-[2px] py-[1px]': tabActive === 2,
                }"
                :style="getLabelStyle('production', item)"
              >
                <h4 class="text-slate-800" :class="getClass()">
                  {{ formatFloat(item.production) }}
                </h4>
              </div>
              <div
                v-if="(item.val_amber ?? 0) !== 0 && height"
                class="absolute rounded-lg bg-amber-500 z-10"
                :class="{
                  'px-1 py-[2px]': tabActive === 1,
                  'px-[2px] py-[1px]': tabActive === 2,
                }"
                :style="getLabelStyle('val_amber', item)"
              >
                <h4 class="text-white" :class="getClass()">
                  {{ formatFloat(item.val_amber) }}
                </h4>
              </div>
              <div
                v-if="(item.val_red ?? 0) !== 0 && height"
                class="absolute rounded-lg bg-red-600 z-10"
                :class="{
                  'px-1 py-[2px]': tabActive === 1,
                  'px-[2px] py-[1px]': tabActive === 2,
                }"
                :style="getLabelStyle('val_red', item)"
              >
                <h4 class="text-white" :class="getClass()">
                  {{ formatFloat(item.val_red) }}
                </h4>
              </div>
              <div
                v-if="(item.val_orange ?? 0) !== 0 && height"
                class="absolute rounded-lg bg-orange-200 z-10"
                :class="{
                  'px-1 py-[2px]': tabActive === 1,
                  'px-[2px] py-[1px]': tabActive === 2,
                }"
                :style="getLabelStyle('val_orange', item)"
              >
                <h4 class="text-white" :class="getClass()">
                  {{ formatFloat(item.val_orange) }}
                </h4>
              </div>
              <div
                v-if="(item.val_emerald ?? 0) !== 0 && height"
                class="absolute rounded-lg bg-emerald-600 z-10"
                :class="{
                  'px-1 py-[2px]': tabActive === 1,
                  'px-[2px] py-[1px]': tabActive === 2,
                }"
                :style="getLabelStyle('val_emerald', item)"
              >
                <h4 class="text-white" :class="getClass()">
                  {{ formatFloat(item.val_emerald) }}
                </h4>
              </div>
              <div
                v-if="(item.val_yellow ?? 0) !== 0 && height"
                class="absolute rounded-lg bg-orange-300 z-10"
                :class="{
                  'px-1 py-[2px]': tabActive === 1,
                  'px-[2px] py-[1px]': tabActive === 2,
                }"
                :style="getLabelStyle('val_yellow', item)"
              >
                <h4 class="text-white" :class="getClass()">
                  {{ formatFloat(item.val_yellow) }}
                </h4>
              </div>

              <div
                class="absolute rounded-lg bg-blue-900 left-1/2 -translate-x-1/2 z-20"
                :class="{
                  'px-1 py-[1px]': tabActive === 1,
                  'px-[2px] py-[2px]': tabActive === 2,
                }"
                :style="{
                  bottom:
                    calculateHeight(item.dot_blue) -
                    (tabActive === 2 ? 6 : 7) +
                    'px',
                }"
                ref="dots"
                v-if="legend.find((data) => data.id === idRatioLegend).show"
              >
                <h4 class="text-white" :class="getClass()">
                  {{ formatFloat(item.dot_blue) }}
                </h4>
              </div>

              <div class="absolute -bottom-4 w-full">
                <h4 class="text-gray-500 text-center" :class="getClass()">
                  {{ item.title }}
                </h4>
              </div>
            </div>
          </div>
        </div>

        <div
          class="px-3 grid gap-1 pt-2"
          :style="`grid-template-columns: repeat(${legend.length}, 1fr)`"
        >
          <div
            class="flex gap-1 items-start"
            v-for="item in legend"
            :key="item.id"
          >
            <div
              v-if="item.id !== 0"
              class="min-w-2 w-2 h-2 mt-[0.7px] border"
              :class="{
                'bg-slate-200': item.id === 1,
                'bg-amber-500': item.id === 2,
                'bg-red-600': item.id === 3,
                'bg-orange-200': item.id === 4,
                'bg-emerald-600': item.id === 5,
                'bg-blue-900': item.id === 6,
                'bg-orange-300': item.id === 7,
              }"
            ></div>
            <p
              class="text-[9px] text-gray-500 font-normal leading-none"
              :class="{
                'font-semibold text-slate-800 text-[10px]': item.id === 0,
              }"
            >
              {{ item.title }}
            </p>
          </div>
        </div>
      </template>
      <template v-else>
        <div
          class="w-full my-4 overflow-x-auto overflow-y-hidden relative px-2"
          :style="{ height: height + 50 + 'px' }"
        >
          <div class="flex items-end gap-5 min-w-max relative h-full">
            <div
              class="w-32 flex justify-between items-end relative mb-4"
              v-for="(data, index) in compareData"
              :key="index"
            >
              <div
                class="w-9 bg-red-600 rounded-t relative flex justify-center"
                :style="{ height: calculateHeightCompare(data.fuel) + 'px' }"
                :class="[displayCompare.fuel ? 'opacity-100' : 'opacity-0']"
              >
                <div
                  class="absolute text-xs text-slate-800 font-semibold -top-4"
                >
                  {{ data.fuel }}
                </div>
              </div>
              <div
                class="w-9 bg-red-900 rounded-t relative flex justify-center"
                :style="{
                  height: calculateHeightCompare(data.distance) + 'px',
                }"
                :class="[displayCompare.distance ? 'opacity-100' : 'opacity-0']"
              >
                <div
                  class="absolute text-xs text-slate-800 font-semibold -top-4"
                >
                  {{ data.distance }}
                </div>
              </div>
              <div
                class="w-9 bg-gray-400 rounded-t relative flex justify-center"
                :style="{ height: calculateHeightCompare(data.ob_coal) + 'px' }"
                :class="[displayCompare.ob_coal ? 'opacity-100' : 'opacity-0']"
              >
                <div
                  class="absolute text-xs text-slate-800 font-semibold -top-4"
                >
                  {{ data.ob_coal }}
                </div>
              </div>

              <div class="absolute -bottom-4 w-full">
                <h4
                  class="text-slate-700 text-center text-[10px] font-semibold"
                >
                  {{ data.initials }}
                </h4>
              </div>
            </div>

            <div
              class="absolute bottom-4 left-0 right-0 border-t border-gray-300 h-px"
            ></div>
          </div>
        </div>
      </template>
    </v-card>
  </div>
</template>

<script>
import { formatFloat } from "@/utilities/helpers";

export default {
  name: "CardChartComponent",
  data() {
    return {
      polylinePoints: "",
      chartViews: [
        {
          id: 1,
          title: "Quarterly",
        },
        {
          id: 2,
          title: "Monthly",
        },
      ],
      tabActive: 1,
      compare: false,
      mitra: [0],
      displayCompare: {
        fuel: true,
        distance: true,
        ob_coal: true,
      },
    };
  },
  props: {
    height: Number,
    idRatioLegend: Number,
    title: String,
    icon: String,
    bgColorIcon: String,
    isLarge: Boolean,
    type: String,
    legend: Array,
    data: Array,
    compareData: Array,
    initialData: Array,
    isAdmin: Boolean,
    loading: Boolean,
    getChart: Function,
    getCompareChart: Function,
  },
  watch: {
    data: {
      handler(newData) {
        if (newData) {
          this.updatePolyline();
        }
      },
      deep: true,
      immediate: true,
    },
  },
  mounted() {
    this.updatePolyline();
    window.addEventListener("resize", this.updatePolyline);
  },
  unmounted() {
    window.removeEventListener("resize", this.updatePolyline);
  },
  methods: {
    formatFloat,
    updatePolyline() {
      this.$nextTick(() => {
        const dots = this.$refs.dots;
        const blocks = this.$refs.blocks;

        if (!dots || !blocks) return;

        const dotsArray = Array.isArray(dots) ? dots : [dots];
        const blocksArray = Array.isArray(blocks) ? blocks : [blocks];

        const svgContainer = blocksArray[0]?.offsetParent;

        const points = dotsArray.map((dot) => {
          const dotEl = dot;
          const parentRect = svgContainer.getBoundingClientRect();
          const dotRect = dotEl.getBoundingClientRect();

          const x = dotRect.left + dotRect.width / 2 - parentRect.left;
          const y = dotRect.top + dotRect.height / 2 - parentRect.top;

          return `${x},${y}`;
        });

        this.polylinePoints = points.join(" ");
      });
    },
    calculateZIndex(item) {
      const fields = Object.keys(item).filter(
        (key) => key.startsWith("val_") || key.startsWith("production")
      );

      const values = fields.map((key) => ({
        key: key,
        value: parseFloat(item[key]),
      }));

      const sorted = [...values].sort((a, b) => a.value - b.value);

      const zIndexes = {};
      sorted.forEach((item, index) => {
        zIndexes[item.key] = fields.length - index;
      });

      return zIndexes;
    },
    calculateHeight(item) {
      const maxValue = this.maxValue();
      if (!maxValue || isNaN(maxValue) || maxValue === 0) {
        return 0;
      }

      const height = (item / maxValue) * this.height;

      return isNaN(height) || !isFinite(height) ? 0 : height;
    },
    maxValue() {
      return this.data.reduce((max, d) => {
        return Math.max(max, d.production, d.val_amber, d.val_red);
      }, -Infinity);
    },
    getClass() {
      if (this.tabActive === 1 && !this.isLarge)
        return "text-[10px] font-semibold";
      if (this.tabActive === 2 && !this.isLarge)
        return "text-[6px] font-medium";
      if (this.tabActive === 1 && this.isLarge) return "text-xs font-semibold";
      if (this.tabActive === 2 && this.isLarge)
        return "text-[10px] font-semibold";
      return "";
    },
    calculatePosition(key, item) {
      const maxVal = this.maxValue();
      const heightMax = this.height;

      const valueMap = {
        production: item.production,
        val_amber: item.val_amber,
        val_red: item.val_red,
        val_orange: item.val_orange,
        val_emerald: item.val_emerald,
        val_yellow: item.val_yellow,
      };

      const heightMap = {};
      for (const k in valueMap) {
        let height = (valueMap[k] / maxVal) * heightMax;

        // Tinggi minimal supaya kelihatan, misal indikator kecil
        const minVisualHeight = 20;
        if (height > 0 && height < minVisualHeight) {
          height = minVisualHeight;
        }

        heightMap[k] = height;
      }

      const minGap = this.isLarge ? 20 : 10;
      const ignoreGapIfSmallerThan = 21; // Kalau tinggi blok di bawah ini, boleh tumpuk

      const sortedKeys = Object.keys(heightMap).sort(
        (a, b) => heightMap[b] - heightMap[a]
      );

      for (let i = 1; i < sortedKeys.length; i++) {
        const prevKey = sortedKeys[i - 1];
        const currKey = sortedKeys[i];

        // Cek apakah blok besar atau kecil
        const prevIsLarge = heightMap[prevKey] >= ignoreGapIfSmallerThan;
        const currIsLarge = heightMap[currKey] >= ignoreGapIfSmallerThan;

        if (prevIsLarge && currIsLarge) {
          // Kalau dua-duanya besar, jaga jarak
          if (heightMap[prevKey] - heightMap[currKey] < minGap) {
            heightMap[currKey] = heightMap[prevKey] - minGap;

            // Tetap pastikan minimal tinggi visual
            const minVisualHeight = 5;
            if (heightMap[currKey] < minVisualHeight) {
              heightMap[currKey] = minVisualHeight;
            }
          }
        }
        // Kalau salah satu kecil, boleh tumpuk, gak perlu paksa jarak
      }

      return heightMap[key];
    },
    getAdjustedLabelPositions(item) {
      const labels = [
        { key: "production", value: item.production },
        { key: "val_amber", value: item.val_amber },
        { key: "val_red", value: item.val_red },
        { key: "val_orange", value: item.val_orange },
        { key: "val_emerald", value: item.val_emerald },
        { key: "val_yellow", value: item.val_yellow },
      ].filter((b) => b.value > 0);

      const smallThreshold = 20;

      const positions = labels.map((b) => {
        const pos = this.calculatePosition(b.key, item);
        const blockHeight = this.calculateHeight(item[b.key]);
        const labelHeight = this.isLarge ? 20 : 12;
        const safePadding = 4;
        const safePos = Math.max(blockHeight - labelHeight - safePadding, 0);

        return {
          ...b,
          rawPos: pos,
          safePos,
          blockHeight,
          isSmall: blockHeight <= smallThreshold,
        };
      });

      // Urutkan dari posisi tertinggi (label paling atas)
      positions.sort((a, b) => b.rawPos - a.rawPos);

      const minGap = this.isLarge ? 16 : 10;
      const finalPositions = [];

      positions.forEach((p, index) => {
        let bottom = p.rawPos;

        if (p.isSmall) {
          bottom = 6; // Label kecil selalu rata bawah
        } else if (index > 0) {
          const prev = finalPositions[index - 1];
          if (prev.bottom - bottom < minGap) {
            bottom = prev.bottom - minGap;
          }
        }

        if (!p.isSmall && bottom > p.safePos) {
          bottom = p.safePos;
        }

        finalPositions.push({
          ...p,
          bottom,
        });
      });

      return finalPositions;
    },
    getLabelStyle(key, item) {
      const adjusted = this.getAdjustedLabelPositions(item);
      const current = adjusted.find((b) => b.key === key);

      if (!current) return { display: "none" };

      // Cek blok kecil untuk keperluan berdampingan
      const smallBlocks = this.getSmallBlocks(item);
      const smallCurrent = smallBlocks.find((b) => b.key === key);

      let offsetX = 0;
      if (smallCurrent) {
        const spacing = this.isLarge ? 25 : 18;
        offsetX = (smallCurrent.index - (smallCurrent.total - 1) / 2) * spacing;
      }

      const translateY = this.isLarge ? "translateY(2px)" : "translateY(1px)";

      return {
        bottom: `${current.bottom}px`,
        left: `calc(50% + ${offsetX}px)`,
        transform: `translateX(-50%) ${translateY}`,
      };
    },
    getSmallBlocks(item) {
      const allBlocks = [
        { key: "production", value: item.production },
        { key: "val_amber", value: item.val_amber },
        { key: "val_red", value: item.val_red },
        { key: "val_orange", value: item.val_orange },
        { key: "val_emerald", value: item.val_emerald },
        { key: "val_yellow", value: item.val_yellow },
      ];

      const smallThreshold = 20; // Ambang batas blok kecil

      return allBlocks
        .filter((b) => {
          if (!b.value) return false;
          const blockHeight = this.calculateHeight(item[b.key]);
          return blockHeight <= smallThreshold;
        })
        .map((b, idx, arr) => ({
          ...b,
          index: idx,
          total: arr.length,
        }));
    },
    calculateHeightCompare(item) {
      const maxValue = this.compareData.reduce((max, d) => {
        return Math.max(max, d.fuel, d.distance, d.ob_coal);
      }, -Infinity);

      return (item / maxValue) * this.height;
    },
    getCompare() {
      this.compare = !this.compare;
      if (this.compare && this.type === "FUEL") {
        this.getCompareChart(this.mitra);
      } else {
        this.changeView(this.tabActive);
      }
    },
    changeView(id) {
      this.tabActive = id;
      const quarterly = this.tabActive === 1;
      this.getChart(this.type, quarterly);
    },
  },
};
</script>
